import type {ConfiguredImageProviders} from '@nuxt/image'

import type {Media} from '~/types/cms'

/**
 * Resolves a Payload relationship field from its union type `number | T | null | undefined`
 * to the populated object `T`, or `null` if unpopulated / missing.
 *
 * After querying with `depth >= 1` relations are always objects at runtime,
 * but TypeScript still sees the `number |` union. This helper narrows it safely.
 */
export function resolveRelation<T extends object>(value: number | T | null | undefined): T | null {
  if (value == null || typeof value === 'number') return null
  return value
}

/**
 * Resolves an array of Payload relationship items, filtering out any
 * unpopulated (numeric ID) entries.
 */
export function resolveRelations<T extends object>(items: (number | T)[] | null | undefined): T[] {
  if (!items) return []
  return items.filter((item): item is T => typeof item !== 'number')
}

/** Extract the `url` from a `Media` relationship field. */
export function resolveMediaUrl(value: number | Media | null | undefined): string | undefined {
  const media = resolveRelation(value)
  if (!media?.url) return undefined

  return media.url
}

/** Extract the `alt` text from a `Media` relationship field. */
export function resolveMediaAlt(value: number | Media | null | undefined): string | undefined {
  const media = resolveRelation(value)
  return media?.alt ?? undefined
}

/**
 * Returns `{ src, alt }` props for a `Media` relationship field.
 * Intended for use with `v-bind="resolveMediaProps(media)"` on `<NuxtImg>` or `<img>`.
 * Returns `undefined` when the media is missing or unpopulated.
 */
export function resolveMediaProps(
  value: number | Media | null | undefined
): {src: string; alt: string; provider: keyof ConfiguredImageProviders} | undefined {
  const media = resolveRelation(value)
  if (!media?.url) return undefined
  return {src: media.url, alt: media.alt ?? '', provider: 'cmsIpx'}
}
